import { useMemo, useState, useEffect } from 'react';
import { useAuthState } from '@packages/contexts/auth';
import { getStisSubscriptionExpiry, type StisSubscriptionExpiry } from '@web/utils/can-view-stis';

export interface UseStisSubscriptionExpiryResult extends Omit<StisSubscriptionExpiry, never> {
    isDismissed: boolean;
    dismiss: () => void;
}

/**
 * Provides STIS subscription expiry state for the current 3rd-party user.
 * Returns null when the account is not a 3rd-party user or there is no expiry
 * data to show.
 *
 * `isDismissed` / `dismiss` give per-session banner suppression so the user
 * can close the banner without it reappearing on every navigation.
 */
export const useStisSubscriptionExpiry = (): UseStisSubscriptionExpiryResult | null => {
    const { account } = useAuthState();
    const [isDismissed, setIsDismissed] = useState(false);

    // Re-evaluate whenever the account changes (e.g. after login or refresh)
    const expiry = useMemo(() => getStisSubscriptionExpiry(account), [account]);

    // Reset dismiss state when the account changes so a newly-logged-in
    // 3rd-party user always sees the banner on first load.
    useEffect(() => {
        setIsDismissed(false);
    }, [account?.accountId]);

    if (!expiry || !expiry.shouldShowBanner) return null;

    return {
        ...expiry,
        isDismissed,
        dismiss: () => setIsDismissed(true),
    };
};
